/**
 * Personal AI Trainer — Phase 1 (+ AI Coach engine)
 *
 * Pose engine hook. Loads MediaPipe **before** the camera starts, runs
 * inference on requestAnimationFrame only, and survives React StrictMode
 * via ref guards.
 *
 * Returns:
 *   • status:        'idle' | 'loading' | 'permission_denied' | 'no_camera'
 *                  | 'unsupported' | 'error' | 'ready'
 *   • detected:      boolean — person currently in frame
 *   • reps:          integer rep count for the active set
 *   • partialReps:   transient half-rep count (clears each rep)
 *   • holdSeconds:   integer (hold-mode exercises, e.g. plank)
 *   • formBar:       'green' | 'amber' | 'red'
 *   • fpsMode:       'standard' | 'reduced' | 'fallback'
 *   • issueKey:      most recent form issue key (for suggestion engine)
 *   • formScore:     0-100 validation score for matched exercises, else null
 *   • feedbackMessage: live coaching text for matched exercises, else ""
 *   • greenPct etc.  rolling form-bar distribution
 *   • controls:      start(videoEl, canvasEl?), stop(), resetSet(), retry()
 *
 * Exercise classification/detection is driven by aiCoachEngine.js — a port of
 * the AI-Personal-Coach project's rule-based pipeline, covering 101 exercises
 * with real joint-angle rep counting and form scoring. Exercises not found in
 * that registry fall back to a generic hip-oscillation rep counter with the
 * form bar locked green (unchanged legacy behavior for unmatched names).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeExerciseKey, AICoachSession } from "./aiCoachEngine";

// ─── Lazy MediaPipe import (model + tasks-vision are large) ───────────
let _vision = null;
let _PoseLandmarker = null;
async function loadMediaPipe() {
  if (_vision && _PoseLandmarker) return { vision: _vision, PoseLandmarker: _PoseLandmarker };
  const mod = await import("@mediapipe/tasks-vision");
  _PoseLandmarker = mod.PoseLandmarker;
  _vision = await mod.FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm"
  );
  return { vision: _vision, PoseLandmarker: _PoseLandmarker };
}

// MediaPipe Pose 33-landmark map
const L = {
  LSHO: 11, RSHO: 12,
  LELB: 13, RELB: 14,
  LWRI: 15, RWRI: 16,
  LHIP: 23, RHIP: 24,
  LKNE: 25, RKNE: 26,
  LANK: 27, RANK: 28,
  LFI: 31, RFI: 32, // foot index
};

// ─── Overlay rendering — a faithful port of AI-Personal-Coach/overlay.py's
// render_overlay(): full 33-point skeleton, top-left structured info panel
// (exercise name, live/weak-signal indicator, posture, primary/secondary
// joint angles, validation score, wrapped feedback text, time/FPS footer),
// top-right stats badge (form-score ring + reps/hold), and center banners
// for "No Person Detected" / "Wrong Exercise". Colors are the RGB
// equivalents of the Python app's BGR palette. ──────────────────────────
const OV = {
  good: "#5AD26E", warning: "#FAB932", critical: "#EB4B4B",
  cyan: "#50D2EB", gold: "#F5C81E",
  textPrimary: "#EBEBEB", textSecondary: "#A0A0A0", textMuted: "#6E6E6E",
  panelBg: "rgba(22,20,18,0.72)", panelBorder: "rgba(75,72,68,0.9)",
};
const MIN_LANDMARK_VISIBILITY = 0.3;
// Full 33-point pose skeleton connections, matching overlay.py's _POSE_CONNECTIONS.
const POSE_CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8],
  [9, 10],
  [11, 12], [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
  [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
  [11, 23], [12, 24], [23, 24], [23, 25], [24, 26], [25, 27], [26, 28],
  [27, 29], [28, 30], [27, 31], [28, 32], [29, 31], [30, 32],
];
const EXTREMITY_INDICES = new Set([15, 16, 17, 18, 19, 20, 21, 22, 27, 28, 29, 30, 31, 32]);

// The canvas's drawing buffer is sized to match its own CSS box (see the
// resize logic in the RAF loop), NOT the camera's native resolution — but
// MediaPipe landmarks are normalized (0-1) against the camera's native
// frame, and the <video> beneath the canvas is stretched/cropped into that
// same CSS box via `object-cover`. Points must go through the exact same
// scale-to-cover + center-crop math the browser applies to the video, or
// they land in the wrong place whenever the camera's aspect ratio doesn't
// exactly match the on-screen box (nearly always, in practice) — this was
// the "panels/skeleton don't line up with what's actually visible" bug.
function computeCoverTransform(videoW, videoH, boxW, boxH) {
  if (!videoW || !videoH || !boxW || !boxH) return { scale: 1, offsetX: 0, offsetY: 0 };
  const scale = Math.max(boxW / videoW, boxH / videoH);
  return { scale, offsetX: (videoW * scale - boxW) / 2, offsetY: (videoH * scale - boxH) / 2 };
}

// The video/canvas display is CSS-mirrored (scaleX(-1)) for a natural
// selfie-view, but MediaPipe returns landmarks in the raw, unmirrored camera
// space. Text drawn on a CSS-mirrored canvas would render backwards, so the
// canvas itself is NOT mirrored — instead every landmark x-coordinate is
// flipped here (in normalized video space, before the cover transform) to
// match what the mirrored video looks like, while text/UI chrome is drawn
// normally and stays legible.
function mapPoint(p, videoW, videoH, cover) {
  return {
    x: (1 - p.x) * videoW * cover.scale - cover.offsetX,
    y: p.y * videoH * cover.scale - cover.offsetY,
  };
}

function scoreColor(v) {
  if (v == null) return OV.textSecondary; // no score available — neutral, not "bad"
  if (v >= 80) return OV.good;
  if (v >= 60) return OV.warning;
  return OV.critical;
}
function postureColor(posture) {
  if (posture === "EXCELLENT" || posture === "GOOD") return OV.good;
  if (posture === "FAIR") return OV.warning;
  return OV.critical;
}

function roundRectPath(ctx, x, y, w, h, r) {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}
function drawPanelCard(ctx, x, y, w, h, accent) {
  roundRectPath(ctx, x, y, w, h, 10);
  ctx.fillStyle = OV.panelBg;
  ctx.fill();
  ctx.strokeStyle = OV.panelBorder;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = accent;
  ctx.fillRect(x, y + 10, 3, Math.max(0, h - 20));
}
function drawRingGauge(ctx, cx, cy, radius, thickness, value, color) {
  ctx.strokeStyle = OV.panelBorder;
  ctx.lineWidth = thickness;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
  if (value != null) {
    const clamped = Math.max(0, Math.min(100, value));
    const sweep = (clamped / 100) * Math.PI * 2;
    if (sweep > 0.01) {
      ctx.strokeStyle = color;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, -Math.PI / 2, -Math.PI / 2 + sweep);
      ctx.stroke();
    }
  }
  ctx.fillStyle = OV.textPrimary;
  ctx.font = "bold 16px 'Barlow Condensed', sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(value == null ? "–" : String(Math.round(value)), cx, cy + 1);
}
function wrapText(ctx, text, maxWidth) {
  const words = (text || "").split(" ").filter(Boolean);
  const lines = [];
  let current = "";
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (ctx.measureText(candidate).width <= maxWidth || !current) current = candidate;
    else { lines.push(current); current = word; }
  }
  if (current) lines.push(current);
  return lines;
}

function drawSkeletonPoints(ctx, lm, videoW, videoH, cover) {
  const at = (p) => mapPoint(p, videoW, videoH, cover);
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = OV.textSecondary;
  for (const [a, b] of POSE_CONNECTIONS) {
    const pa = lm[a], pb = lm[b];
    if (!pa || !pb || (pa.visibility ?? 1) < MIN_LANDMARK_VISIBILITY || (pb.visibility ?? 1) < MIN_LANDMARK_VISIBILITY) continue;
    const a1 = at(pa), b1 = at(pb);
    ctx.beginPath();
    ctx.moveTo(a1.x, a1.y);
    ctx.lineTo(b1.x, b1.y);
    ctx.stroke();
  }
  const p11 = lm[11], p12 = lm[12], p23 = lm[23], p24 = lm[24];
  const vis = (p) => (p?.visibility ?? 0) >= MIN_LANDMARK_VISIBILITY;
  if (p11 && p12 && vis(p11) && vis(p12)) {
    const a1 = at(p11), b1 = at(p12);
    ctx.strokeStyle = OV.gold;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(a1.x, a1.y);
    ctx.lineTo(b1.x, b1.y);
    ctx.stroke();
  }
  if (p11 && p12 && p23 && p24 && [p11, p12, p23, p24].every(vis)) {
    const shoulderMid = at({ x: (p11.x + p12.x) / 2, y: (p11.y + p12.y) / 2 });
    const hipMid = at({ x: (p23.x + p24.x) / 2, y: (p23.y + p24.y) / 2 });
    ctx.strokeStyle = OV.cyan;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(shoulderMid.x, shoulderMid.y);
    ctx.lineTo(hipMid.x, hipMid.y);
    ctx.stroke();
  }
  for (let i = 0; i < lm.length; i++) {
    const p = lm[i];
    if (!p || (p.visibility ?? 1) < MIN_LANDMARK_VISIBILITY) continue;
    const { x: px, y: py } = at(p);
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.arc(px, py, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = EXTREMITY_INDICES.has(i) ? OV.gold : OV.cyan;
    ctx.beginPath();
    ctx.arc(px, py, 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawStatsBadge(ctx, width, formScore, secondaryLabel, secondaryValue) {
  const badgeW = 132, margin = 18, ringR = 34, ringT = 5;
  const x2 = width - margin, x1 = x2 - badgeW, y1 = margin;
  const color = scoreColor(formScore);
  const ringCy = y1 + 16 + ringR;
  const capY = ringCy + ringR + 18;
  const divY = capY + 8;
  const secY = divY + 28;
  const secCapY = secY + 16;
  const y2 = secCapY + 10;

  drawPanelCard(ctx, x1, y1, badgeW, y2 - y1, color);
  drawRingGauge(ctx, x1 + badgeW / 2, ringCy, ringR, ringT, formScore, color);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = OV.textSecondary;
  ctx.font = "10px Barlow, sans-serif";
  ctx.fillText("FORM", x1 + badgeW / 2, capY);

  ctx.strokeStyle = OV.panelBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x1 + 16, divY);
  ctx.lineTo(x2 - 16, divY);
  ctx.stroke();

  ctx.fillStyle = OV.textPrimary;
  ctx.font = "bold 20px 'Barlow Condensed', sans-serif";
  ctx.fillText(secondaryValue, x1 + badgeW / 2, secY);
  ctx.fillStyle = OV.textSecondary;
  ctx.font = "10px Barlow, sans-serif";
  ctx.fillText(secondaryLabel, x1 + badgeW / 2, secCapY);
  ctx.textAlign = "left";
}

function drawInfoPanel(ctx, width, opts) {
  const { exerciseName, posture, primaryAngle, secondaryAngle, validationScore, confidence, message, level, elapsedSeconds, fps } = opts;
  const margin = 18, panelW = Math.min(280, width - margin * 2), padding = 14;
  if (panelW <= 40) return;
  const x1 = margin, x2 = x1 + panelW;

  const rows = [];
  if (primaryAngle != null) rows.push(["PRIMARY ANGLE", `${Math.round(primaryAngle)}°`, OV.textPrimary]);
  if (secondaryAngle != null) rows.push(["SECONDARY ANGLE", `${Math.round(secondaryAngle)}°`, OV.textPrimary]);
  if (validationScore != null) rows.push(["VALIDATION SCORE", `${Math.round(validationScore)}`, scoreColor(validationScore)]);

  ctx.font = "11px Barlow, sans-serif";
  const feedbackLines = message ? wrapText(ctx, message, panelW - 2 * padding) : [];

  const rowH = 20;
  const bodyH =
    26 + (posture ? rowH : 0) + 8 + rows.length * rowH +
    (feedbackLines.length ? 8 + feedbackLines.length * 16 : 0) + 8 + 16 + padding;
  const y1 = margin, y2 = y1 + padding + bodyH;
  const accent = posture ? postureColor(posture) : OV.cyan;
  drawPanelCard(ctx, x1, y1, panelW, y2 - y1, accent);

  let cy = y1 + padding + 12;
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = OV.textPrimary;
  ctx.font = "bold 14px 'Barlow Condensed', sans-serif";
  ctx.fillText((exerciseName || "No Exercise Selected").toUpperCase(), x1 + padding, cy);

  const liveColor = confidence >= 60 ? OV.good : confidence >= 30 ? OV.warning : OV.critical;
  const liveLabel = confidence >= 60 ? "LIVE" : confidence >= 30 ? "WEAK SIGNAL" : "NO SIGNAL";
  ctx.font = "10px Barlow, sans-serif";
  ctx.fillStyle = liveColor;
  ctx.textAlign = "right";
  ctx.fillText(liveLabel, x2 - padding, cy);
  const labelW = ctx.measureText(liveLabel).width;
  ctx.beginPath();
  ctx.fillStyle = liveColor;
  ctx.arc(x2 - padding - labelW - 9, cy - 3, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.textAlign = "left";

  cy += 20;
  if (posture) {
    ctx.fillStyle = postureColor(posture);
    ctx.font = "11px Barlow, sans-serif";
    ctx.fillText(`Posture: ${posture.charAt(0) + posture.slice(1).toLowerCase()}`, x1 + padding, cy);
    cy += rowH;
  }
  ctx.strokeStyle = OV.panelBorder;
  ctx.beginPath();
  ctx.moveTo(x1 + padding, cy - 12);
  ctx.lineTo(x2 - padding, cy - 12);
  ctx.stroke();
  cy += 4;

  for (const [label, value, color] of rows) {
    ctx.fillStyle = OV.textSecondary;
    ctx.font = "10px Barlow, sans-serif";
    ctx.textAlign = "left";
    ctx.fillText(label, x1 + padding, cy);
    ctx.fillStyle = color;
    ctx.textAlign = "right";
    ctx.fillText(value, x2 - padding, cy);
    ctx.textAlign = "left";
    cy += rowH;
  }

  if (feedbackLines.length) {
    ctx.strokeStyle = OV.panelBorder;
    ctx.beginPath();
    ctx.moveTo(x1 + padding, cy - 12);
    ctx.lineTo(x2 - padding, cy - 12);
    ctx.stroke();
    cy += 4;
    ctx.font = "11px Barlow, sans-serif";
    ctx.fillStyle = level === "ERROR" ? OV.critical : level === "WARNING" ? OV.warning : level === "SUCCESS" ? OV.good : OV.cyan;
    for (const line of feedbackLines) {
      ctx.fillText(line, x1 + padding, cy);
      cy += 16;
    }
  }

  ctx.strokeStyle = OV.panelBorder;
  ctx.beginPath();
  ctx.moveTo(x1 + padding, cy - 10);
  ctx.lineTo(x2 - padding, cy - 10);
  ctx.stroke();
  cy += 4;
  ctx.fillStyle = OV.textMuted;
  ctx.font = "10px Barlow, sans-serif";
  const mm = String(Math.floor((elapsedSeconds || 0) / 60)).padStart(2, "0");
  const ss = String(Math.floor((elapsedSeconds || 0) % 60)).padStart(2, "0");
  ctx.fillText(`Time ${mm}:${ss}   ·   FPS ${Math.round(fps || 0)}`, x1 + padding, cy);
}

function drawCenterMessage(ctx, width, height, message, color, subtitle) {
  ctx.font = "bold 18px Barlow, sans-serif";
  const tw = ctx.measureText(message).width;
  let sw = 0;
  if (subtitle) { ctx.font = "12px Barlow, sans-serif"; sw = ctx.measureText(subtitle).width; }
  const padX = 22, padY = 16;
  const boxW = Math.max(tw, sw) + padX * 2;
  const boxH = 40 + (subtitle ? 24 : 0);
  const x1 = (width - boxW) / 2, y1 = (height - boxH) / 2;
  drawPanelCard(ctx, x1, y1, boxW, boxH, color);
  ctx.textAlign = "center";
  ctx.fillStyle = color;
  ctx.font = "bold 18px Barlow, sans-serif";
  ctx.fillText(message, width / 2, y1 + padY + 14);
  if (subtitle) {
    ctx.fillStyle = OV.textSecondary;
    ctx.font = "12px Barlow, sans-serif";
    ctx.fillText(subtitle, width / 2, y1 + padY + 14 + 22);
  }
  ctx.textAlign = "left";
}

// Full replacement for the Python app's render_overlay(): draws the
// skeleton, info panel, stats badge, and any center banner in one pass.
function drawOverlay(canvas, opts) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const { width, height } = canvas;
  if (!width || !height) return;
  ctx.clearRect(0, 0, width, height);

  const { lm, videoW, videoH, exerciseName, analysis, validationScore, confidence, message, level, reps, holdTime, elapsedSeconds, fps, wrongExercise } = opts;
  const landmarksPresent = !!lm && lm.some((p) => (p?.visibility ?? 0) >= MIN_LANDMARK_VISIBILITY);

  if (landmarksPresent) {
    const cover = computeCoverTransform(videoW, videoH, width, height);
    drawSkeletonPoints(ctx, lm, videoW, videoH, cover);
  }

  // Desktop-style HUD panels need real estate — below this, just show the
  // skeleton (e.g. the small PiP camera window has its own compact number).
  const hasRoomForPanels = width >= 260;
  if (hasRoomForPanels) {
    drawInfoPanel(ctx, width, {
      exerciseName,
      posture: analysis?.posture,
      primaryAngle: analysis?.primaryAngle,
      secondaryAngle: analysis?.secondaryAngle,
      validationScore,
      confidence: confidence || 0,
      message,
      level,
      elapsedSeconds,
      fps,
    });

    const isHold = holdTime != null && holdTime > 0;
    drawStatsBadge(ctx, width, analysis?.formScore ?? validationScore, isHold ? "HOLD" : "REPS", isHold ? `${Math.round(holdTime)}s` : `${reps ?? 0}`);

    if (!landmarksPresent) {
      drawCenterMessage(ctx, width, height, "No Person Detected", OV.critical);
    } else if (wrongExercise) {
      drawCenterMessage(ctx, width, height, "Wrong Exercise", OV.critical, "Selected exercise doesn't match movement");
    }
  }
}

function angle(a, b, c) {
  if (!a || !b || !c) return 0;
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const m = Math.hypot(ab.x, ab.y) * Math.hypot(cb.x, cb.y);
  if (!m) return 0;
  return (Math.acos(Math.max(-1, Math.min(1, dot / m))) * 180) / Math.PI;
}

// Classifier: turns an exercise name into a tracked kind. Prefers a match in
// the ported AI Coach rule registry (101 exercises); unmatched names fall
// back to the generic oscillation counter under kind "other".
export function classifyExercise(name = "") {
  return normalizeExerciseKey(name) || "other";
}

export default function usePersonalAITrainerEngine({ exerciseName = "", aiOn = true } = {}) {
  const [status, setStatus] = useState("idle");
  const [detected, setDetected] = useState(false);
  const [reps, setReps] = useState(0);
  const [partialReps, setPartialReps] = useState(0);
  const [holdSeconds, setHoldSeconds] = useState(0);
  const [formBar, setFormBar] = useState("green");
  const [fpsMode, setFpsMode] = useState("standard");
  const [issueKey, setIssueKey] = useState(null);
  const [formScore, setFormScore] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState("");

  // Refs (avoid StrictMode double-mount races)
  const cameraActiveRef = useRef(false);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const detectorRef = useRef(null);
  const modelReadyRef = useRef(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Oscillation fallback (unmatched exercises only)
  const lastRepAtRef = useRef(0);

  // Velocity / FPS tracking
  const lastHipYRef = useRef(null);
  const lastFrameAtRef = useRef(0);
  const fpsSamplesRef = useRef([]);
  const frameSkipRef = useRef(0);
  const detectFailStreakRef = useRef(0);

  // Form-bar rolling counters
  const greenFramesRef = useRef(0);
  const amberFramesRef = useRef(0);
  const redFramesRef = useRef(0);
  const formStateLockRef = useRef({ state: "green", since: performance.now() });

  // AI Coach session (rich rule-driven engine) — one per matched exercise
  const sessionRef = useRef(null);
  const sessionStartRef = useRef(null);
  const currentFpsRef = useRef(0);

  const kind = classifyExercise(exerciseName);

  // (Re)create the AI Coach session whenever the matched exercise changes
  useEffect(() => {
    sessionRef.current = kind !== "other" ? new AICoachSession(kind) : null;
    sessionStartRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  // ─── Stop / cleanup ────────────────────────────────────────────────
  const stop = useCallback(() => {
    cameraActiveRef.current = false;
    modelReadyRef.current = false;
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch { /* ignore */ }
    }
    if (detectorRef.current?.close) {
      try { detectorRef.current.close(); } catch { /* ignore */ }
    }
    detectorRef.current = null;
    detectFailStreakRef.current = 0;
    setStatus("idle");
    setDetected(false);
  }, []);

  const resetSet = useCallback(() => {
    setReps(0);
    setPartialReps(0);
    setHoldSeconds(0);
    setIssueKey(null);
    setFormScore(null);
    setFeedbackMessage("");
    sessionRef.current?.reset();
    sessionStartRef.current = null;
    oscillationRepsRef.current = 0;
    greenFramesRef.current = 0;
    amberFramesRef.current = 0;
    redFramesRef.current = 0;
  }, []);

  // ─── Inference loop (RAF only) ────────────────────────────────────
  const loop = useCallback(() => {
    if (!cameraActiveRef.current) return;

    const video = videoRef.current;
    if (!modelReadyRef.current || !detectorRef.current || !video || video.readyState < 2) {
      animFrameRef.current = requestAnimationFrame(loop);
      return;
    }

    // Keep the canvas's drawing buffer matched to its own CSS box (not the
    // camera's native resolution — those almost never share an aspect ratio,
    // which is exactly what made the HUD panels land outside the visible
    // area). Checked every frame since the box can resize (full-screen vs
    // PiP toggle, orientation change) independent of the camera stream.
    if (canvasRef.current) {
      const c = canvasRef.current;
      const cw = c.clientWidth, ch = c.clientHeight;
      if (cw && ch && (c.width !== cw || c.height !== ch)) {
        c.width = cw;
        c.height = ch;
      }
    }

    // FPS sampling — measured across first 30 frames
    const now = performance.now();
    if (lastFrameAtRef.current) {
      currentFpsRef.current = 1000 / Math.max(1, now - lastFrameAtRef.current);
      fpsSamplesRef.current.push(currentFpsRef.current);
      if (fpsSamplesRef.current.length === 30) {
        const avg = fpsSamplesRef.current.reduce((a, b) => a + b, 0) / 30;
        if (avg < 15) setFpsMode("fallback");
        else if (avg < 25) setFpsMode("reduced");
        else setFpsMode("standard");
      }
    }
    lastFrameAtRef.current = now;

    // Skip frames in reduced/fallback mode
    const skipNeeded = fpsMode === "fallback" ? 2 : fpsMode === "reduced" ? 1 : 0;
    if (skipNeeded > 0 && frameSkipRef.current < skipNeeded) {
      frameSkipRef.current++;
      animFrameRef.current = requestAnimationFrame(loop);
      return;
    }
    frameSkipRef.current = 0;

    let res = null;
    try {
      res = detectorRef.current.detectForVideo(video, now);
      detectFailStreakRef.current = 0;
    } catch (err) {
      detectFailStreakRef.current += 1;
      if (detectFailStreakRef.current === 1 || detectFailStreakRef.current % 60 === 0) {
        // eslint-disable-next-line no-console
        console.error("[PersonalAITrainer] detectForVideo failed", detectFailStreakRef.current, "times in a row:", err);
      }
      // Sustained failure (~2s at 30fps) means the model/GPU delegate is
      // broken for this session, not a one-off dropped frame — surface it
      // instead of silently retrying forever behind a blank camera view.
      if (detectFailStreakRef.current >= 60) {
        setStatus("error");
        return;
      }
      animFrameRef.current = requestAnimationFrame(loop);
      return;
    }
    const lm = res?.landmarks?.[0];
    const haveLandmarks = !!lm && lm.length >= 33;
    setDetected(haveLandmarks);

    // Everything below this point (rule engine + canvas drawing) is
    // sandboxed: if any of it throws, the RAF loop must NOT die silently —
    // that previously showed as "camera opens, nothing else ever happens",
    // with no visible clue why. Now a crash logs to console and shows a
    // visible on-canvas error instead of freezing the whole overlay forever.
    try {
    if (haveLandmarks) {
      const hipY = (lm[L.LHIP].y + lm[L.RHIP].y) / 2;
      const velocity =
        lastHipYRef.current == null ? 0 : Math.abs(hipY - lastHipYRef.current);
      lastHipYRef.current = hipY;
      const fastMove = velocity > 0.12;

      if (sessionRef.current) {
        // Rich rule-driven engine (aiCoachEngine.js) — debounce/cooldown/
        // min-confidence gating is handled inside the ported RepetitionCounter,
        // so no extra velocity gate is needed here.
        if (sessionStartRef.current == null) sessionStartRef.current = now;
        const result = sessionRef.current.processFrame(lm, now / 1000);
        setReps(result.repResult.reps);
        if (result.analysis.phase === "HOLDING" || result.repResult.holdTime > 0) {
          setHoldSeconds(Math.floor(result.repResult.holdTime));
        }
        setFormScore(Math.round(result.validation.score));
        setFeedbackMessage(result.feedback.message || "");
        applyFormState(fastMove ? null : result.formBar, fastMove);

        const wrongExercise =
          (result.validation.errors || []).some((e) => String(e).toLowerCase().includes("unknown exercise")) ||
          (result.validation.matchedOrientation === false && result.validation.matchedAngles === false);
        drawOverlay(canvasRef.current, {
          lm,
          videoW: video.videoWidth,
          videoH: video.videoHeight,
          exerciseName,
          analysis: result.analysis,
          validationScore: result.validation.score,
          confidence: result.analysis.confidence,
          message: result.feedback.message,
          level: result.feedback.level,
          reps: result.repResult.reps,
          holdTime: result.repResult.holdTime,
          elapsedSeconds: (now - sessionStartRef.current) / 1000,
          fps: currentFpsRef.current,
          wrongExercise,
        });

        // Bespoke fault-detail keys (squat/push_up/plank) still drive the
        // suggestion-sheet UI; every other matched exercise gets real form
        // feedback via result.feedback, just without a named issue key.
        const issue = detectIssue(lm, kind);
        setIssueKey(issue?.key || null);
      } else if (!fastMove) {
        // Unmatched exercise (not one of the 101 the AI Coach engine knows) —
        // still show the exercise name, a clear explanation, and a basic
        // movement-based rep count, instead of leaving the HUD blank with no
        // indication of why detailed tracking/scoring isn't available here.
        if (sessionStartRef.current == null) sessionStartRef.current = now;
        runOscillation(hipY);
        applyFormState("green", fastMove);
        setFormScore(null);
        setFeedbackMessage("");
        setIssueKey(null);
        drawOverlay(canvasRef.current, {
          lm,
          videoW: video.videoWidth,
          videoH: video.videoHeight,
          exerciseName,
          analysis: null,
          validationScore: null,
          confidence: 100,
          message: "No detailed AI tracking for this exercise yet — showing basic movement count.",
          level: "INFO",
          reps: oscillationRepsRef.current,
          holdTime: 0,
          elapsedSeconds: sessionStartRef.current != null ? (now - sessionStartRef.current) / 1000 : 0,
          fps: currentFpsRef.current,
          wrongExercise: false,
        });
      }
    } else {
      // No person detected at all this frame — keep rep/session state, do not
      // regress, but still draw the panel + "No Person Detected" banner so
      // there's always visible feedback instead of a silently blank camera.
      lastHipYRef.current = null;
      drawOverlay(canvasRef.current, {
        lm: null,
        exerciseName,
        analysis: null,
        validationScore: 0,
        confidence: 0,
        message: "",
        level: "INFO",
        reps: sessionRef.current?.repCounter?.getCount() ?? 0,
        holdTime: 0,
        elapsedSeconds: sessionStartRef.current != null ? (now - sessionStartRef.current) / 1000 : 0,
        fps: currentFpsRef.current,
        wrongExercise: false,
      });
    }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[PersonalAITrainer] frame processing crashed:", err);
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx && canvas.width && canvas.height) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = "#EB4B4B";
          ctx.font = "bold 14px sans-serif";
          ctx.textAlign = "left";
          ctx.fillText("AI Coach error — open the browser console", 16, 28);
          ctx.font = "12px sans-serif";
          ctx.fillText(String(err?.message || err).slice(0, 60), 16, 48);
        }
      }
    }

    animFrameRef.current = requestAnimationFrame(loop);
  }, [kind, fpsMode, exerciseName]);

  // NOTE: peak/valley reset to the current point after every direction
  // change, so `range` measures THIS swing only. The original version let
  // peak/valley grow across the whole session without ever resetting, so
  // the detector got MORE sensitive to trivial sway/breathing the longer a
  // session ran — a wide swing from minutes ago left a permanently large
  // "envelope" that tiny, unrelated movements near either extreme could
  // then re-trigger. A hysteresis band (must clear 25% past the midpoint,
  // not just barely cross it) further filters out noise near dead center.
  // This fallback has no rule data for the exercise, so it has no way to
  // tell "real rep" from "shifted weight / breathed / camera jitter" other
  // than requiring a big, sustained, repeated swing. Thresholds are set on
  // purpose much stricter than the rich engine's cooldown/debounce floors
  // (which at least know which joint actually matters) — this signal is
  // just hip Y, which barely moves at all for most of what lands here
  // (curls, presses, rows, pulldowns), so it has to be conservative or
  // ordinary movement while performing the exercise gets misread as reps.
  const MIN_OSCILLATION_RANGE = 0.15;
  const HYSTERESIS = 0.25;
  const OSCILLATION_DEBOUNCE_FRAMES = 3;
  const OSCILLATION_MIN_REP_GAP_MS = 500;
  const runOscillation = useCallback((hipY) => {
    const last = oscillationRef.current;
    if (last.peak == null) {
      oscillationRef.current = { peak: hipY, valley: hipY, lastDir: 0, flips: 0, pendingDir: 0, pendingStreak: 0 };
      return;
    }
    if (hipY > last.peak) last.peak = hipY;
    if (hipY < last.valley) last.valley = hipY;
    const range = last.peak - last.valley;
    if (range < MIN_OSCILLATION_RANGE) return;

    const mid = (last.peak + last.valley) / 2;
    const band = range * HYSTERESIS;
    let rawDir = last.lastDir;
    if (hipY > mid + band) rawDir = 1;
    else if (hipY < mid - band) rawDir = -1;

    // Debounce: a direction only becomes real after several consecutive
    // frames agree — one noisy frame crossing the band must not flip state
    // by itself.
    if (rawDir === last.pendingDir) last.pendingStreak = (last.pendingStreak || 0) + 1;
    else { last.pendingDir = rawDir; last.pendingStreak = 1; }
    const dir = last.pendingStreak >= OSCILLATION_DEBOUNCE_FRAMES ? rawDir : last.lastDir;

    if (last.lastDir !== 0 && dir !== last.lastDir) {
      last.flips = (last.flips || 0) + 1;
      // Start measuring the next swing fresh from here, rather than
      // continuing to accumulate an all-session envelope.
      last.peak = hipY;
      last.valley = hipY;
      if (last.flips % 2 === 0) {
        const now = performance.now();
        if (now - lastRepAtRef.current > OSCILLATION_MIN_REP_GAP_MS) {
          oscillationRepsRef.current += 1;
          setReps(oscillationRepsRef.current);
          lastRepAtRef.current = now;
        }
      }
    }
    last.lastDir = dir;
  }, []);
  const oscillationRef = useRef({ peak: null, valley: null, lastDir: 0, flips: 0, pendingDir: 0, pendingStreak: 0 });
  const oscillationRepsRef = useRef(0);

  // ─── Form bar ─────────────────────────────────────────────────────
  // severity: 'green' | 'amber' | 'red' | null
  const applyFormState = useCallback((severity, fastMove) => {
    let next = "green";
    if (fastMove) next = "green"; // suspend during fast movement
    else if (severity === "red") next = "red";
    else if (severity === "amber") next = "amber";

    // 300ms debounce GOOD→AMBER/RED, immediate revert to GREEN
    const lock = formStateLockRef.current;
    const now = performance.now();
    if (next === "green") {
      formStateLockRef.current = { state: "green", since: now };
      setFormBar("green");
    } else if (lock.state === "green") {
      if (now - lock.since > 300) {
        formStateLockRef.current = { state: next, since: now };
        setFormBar(next);
      }
    } else {
      formStateLockRef.current = { state: next, since: now };
      setFormBar(next);
    }

    // Rolling counters
    if (formBar === "green") greenFramesRef.current++;
    else if (formBar === "amber") amberFramesRef.current++;
    else redFramesRef.current++;
  }, [formBar]);

  // ─── Public: start camera (called after model ready) ──────────────
  const start = useCallback(async (videoEl, canvasEl) => {
    if (cameraActiveRef.current) return;
    if (!aiOn) return;
    cameraActiveRef.current = true;
    videoRef.current = videoEl;
    canvasRef.current = canvasEl;

    setStatus("loading");

    // 1. Quick environment check
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      cameraActiveRef.current = false;
      setStatus("unsupported");
      return;
    }

    try {
      // 2. Load model BEFORE camera
      const { vision, PoseLandmarker } = await loadMediaPipe();
      if (!cameraActiveRef.current) return; // user stopped during load
      detectorRef.current = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath:
            "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task",
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      modelReadyRef.current = true;

      // 3. Only start camera after model ready
      if (!cameraActiveRef.current) return;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      if (!cameraActiveRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      videoEl.srcObject = stream;
      videoEl.onloadedmetadata = () => {
        // Sized to the canvas's own CSS box, not the camera's resolution —
        // see the matching per-frame check in loop() for why.
        if (canvasEl && canvasEl.clientWidth && canvasEl.clientHeight) {
          canvasEl.width = canvasEl.clientWidth;
          canvasEl.height = canvasEl.clientHeight;
        }
      };
      // Deliberately NOT awaited: in some browsers/contexts this promise can
      // hang indefinitely (autoplay policy quirks, backgrounded-tab timing,
      // etc.) even though the stream is already visually rendering. Awaiting
      // it here would silently freeze this entire function before the
      // detection loop is ever scheduled — model, skeleton, panels, rep
      // counting all stuck forever with zero visible sign anything is wrong.
      // loop() already tolerates the video not being ready yet (readyState
      // check, self-reschedules), so there's nothing to gain by waiting.
      videoEl.play().catch((playErr) => {
        // eslint-disable-next-line no-console
        console.error("[PersonalAITrainer] video.play() rejected:", playErr);
      });

      setStatus("ready");
      // 4. Inference loop — tolerates the video/model not being fully ready
      // yet on its first few frames.
      animFrameRef.current = requestAnimationFrame(loop);
    } catch (err) {
      cameraActiveRef.current = false;
      modelReadyRef.current = false;
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") setStatus("permission_denied");
      else if (name === "NotFoundError" || name === "OverconstrainedError") setStatus("no_camera");
      else setStatus("error");
    }
  }, [aiOn, loop]);

  // Auto-cleanup on unmount
  useEffect(() => {
    return () => stop();
  }, [stop]);

  // Reset rep state when exercise changes
  useEffect(() => {
    resetSet();
  }, [exerciseName, resetSet]);

  // Form-bar rolling distribution (for end-of-set telemetry)
  const formBarPct = useMemo(() => {
    const tot = greenFramesRef.current + amberFramesRef.current + redFramesRef.current;
    if (!tot) return { green: 100, amber: 0, red: 0 };
    return {
      green: (greenFramesRef.current / tot) * 100,
      amber: (amberFramesRef.current / tot) * 100,
      red: (redFramesRef.current / tot) * 100,
    };
  }, [reps, formBar, holdSeconds]);

  return {
    status,
    detected,
    reps,
    partialReps,
    holdSeconds,
    formBar,
    fpsMode,
    issueKey,
    formScore,
    feedbackMessage,
    formBarPct,
    kind,
    start,
    stop,
    resetSet,
    retry: () => {
      if (videoRef.current) start(videoRef.current, canvasRef.current);
    },
  };
}

// ─── Issue detection (3 supported exercises only) ───────────────────
function detectIssue(lm, kindKey) {
  if (kindKey === "squat") {
    // Knee tracks over second toe — measured by horizontal divergence vs hip-knee axis
    // Approximation: knee.x relative to hip.x and ankle.x
    const lDelta = (lm[L.LKNE].x - lm[L.LANK].x) - (lm[L.LHIP].x - lm[L.LANK].x);
    const rDelta = (lm[L.RKNE].x - lm[L.RANK].x) - (lm[L.RHIP].x - lm[L.RANK].x);
    const worst = Math.max(Math.abs(lDelta), Math.abs(rDelta));
    if (worst < 0.04) return null;
    if (worst < 0.07) return { key: "knee_cave", severity: "amber" };
    return { key: "knee_cave", severity: "red" };
  }
  if (kindKey === "push_up") {
    // Hip should align with shoulder-ankle line
    const dev = Math.abs(180 - angle(lm[L.LSHO], lm[L.LHIP], lm[L.LANK]));
    if (dev < 8) return null;
    if (dev < 15) return { key: "hip_sag", severity: "amber" };
    return { key: "hip_sag", severity: "red" };
  }
  if (kindKey === "plank") {
    const dev = Math.abs(180 - angle(lm[L.LSHO], lm[L.LHIP], lm[L.LANK]));
    if (dev < 8) return null;
    if (dev < 12) return { key: "hip_deviation", severity: "amber" };
    return { key: "hip_deviation", severity: "red" };
  }
  return null;
}
